/**
 * The legacy WebSocket wire protocol, as pure functions.
 *
 * The old panel's socket handling is a chain of `if (data.type === …)` branches
 * embedded in a hook (`merchant-panel/src/hooks/useWebSocket.ts` plus the
 * dispatch table in its `Routes.tsx`). Reproducing it as data + pure functions
 * means the handshake — which is the part that silently breaks — can be tested
 * without a live server, a real `WebSocket`, or a React tree.
 *
 * The protocol is a four-message handshake:
 *
 *   server → `{"type":"waiting_auth"}`   the socket is up, prove who you are
 *   client → `{"type":"auth", content}`  the legacy access token, verbatim
 *   server → `{"type":"ack_message"}`    authenticated; pushes start flowing
 *   server → `{"type":"auth_failed"}`    rejected; the client must close
 *
 * REJECTION HAS TWO SPELLINGS. Both reference implementations only handle
 * `auth_failed`, but `tback.noor.uz` actually answers a rejected token with
 * `{"type":"error_message","content":"unAuthorized"}` and then drops the
 * connection (verified live 2026-08-18). Treating that as an unknown frame is
 * what turns a permanent rejection into an infinite reconnect loop: the server
 * closes, the client retries, and the cycle repeats forever. Both spellings
 * therefore map to the same terminal action.
 *
 * After `ack_message` the server pushes domain events. `refresh` means "the
 * orders list changed" and carries no payload — it is a signal to refetch, not
 * data to merge. The chat trio (`chat_message`, `new_chat`, `new_chat_status`)
 * drives the merchant-chat drawer the same way the old panel's `Routes.tsx`
 * dispatch table did; their `content` passes through unvalidated and is parsed
 * with Zod at the consumer (`entities/merchant-chat`), so a malformed push
 * fails there without disturbing the socket.
 */

/** A parsed socket frame. `type` is optional: malformed frames are tolerated. */
export interface LegacySocketMessage {
  type?: string;
  content?: unknown;
}

/**
 * What the client should DO about a frame. Deliberately a small closed set
 * rather than the raw message — the caller owns the socket and the query
 * cache, and this module owns the protocol.
 */
export type LegacySocketAction =
  | { kind: 'authenticate' }
  | { kind: 'authenticated' }
  | { kind: 'auth-failed' }
  | { kind: 'refresh' }
  | { kind: 'chat-message'; content: unknown }
  | { kind: 'new-chat'; content: unknown }
  | { kind: 'new-chat-status' }
  | { kind: 'ignore' };

/** The chat subset, for consumers that only care about chat pushes. */
export type LegacyChatSocketAction = Extract<
  LegacySocketAction,
  { kind: 'chat-message' | 'new-chat' | 'new-chat-status' }
>;

/**
 * The server's keepalive answer. It arrives as a BARE STRING, not JSON, so it
 * must be filtered before parsing or every keepalive throws a SyntaxError.
 */
const PONG_FRAME = 'pong';

/**
 * Parse a raw frame into an action.
 *
 * Never throws: a socket that delivers a malformed frame should not take down
 * the connection, and the old panel's unguarded `JSON.parse` is exactly the
 * kind of thing that turns one bad push into a dead orders screen.
 */
export function readSocketFrame(raw: string): LegacySocketAction {
  // `pong` is not JSON. Checked before parsing, as the source does.
  if (raw === PONG_FRAME) return { kind: 'ignore' };

  let message: LegacySocketMessage;
  try {
    message = JSON.parse(raw) as LegacySocketMessage;
  } catch {
    return { kind: 'ignore' };
  }

  switch (message?.type) {
    case 'waiting_auth':
      return { kind: 'authenticate' };
    case 'ack_message':
      return { kind: 'authenticated' };
    case 'auth_failed':
      return { kind: 'auth-failed' };
    // The spelling this backend actually uses — see the header note.
    case 'error_message':
      return { kind: 'auth-failed' };
    case 'refresh':
      return { kind: 'refresh' };
    case 'chat_message':
      return { kind: 'chat-message', content: message.content };
    case 'new_chat':
      return { kind: 'new-chat', content: message.content };
    case 'new_chat_status':
      return { kind: 'new-chat-status' };
    default:
      // Offers and the other cpanel-only event types land here. Nothing in
      // this app renders them, so they are ignored rather than mishandled.
      return { kind: 'ignore' };
  }
}

/** The authentication frame, sent in reply to `waiting_auth`. */
export function authFrame(token: string): string {
  return JSON.stringify({ type: 'auth', content: token });
}

/** The keepalive frame. The server answers with the bare string `pong`. */
export function pingFrame(): string {
  return JSON.stringify({ type: 'PING' });
}
